import React from 'react';
import type { FormField } from './fields';
type Props = {
  fields: FormField[];
  activeId: string | null;
  values: Record<string, string>;
  signatureDataUrl: string | null;
  onFieldClick: (id: string) => void;
};
export function PdfPreview({
  fields,
  activeId,
  values,
  signatureDataUrl,
  onFieldClick
}: Props) {
  return <div className="flex-1 min-w-0 bg-[#0a0a0e] border-r border-white/5 flex flex-col" data-id="element-42">
      {/* PDF toolbar */}
      <div className="px-5 py-2.5 border-b border-white/5 flex items-center justify-between text-[11px] font-mono text-zinc-500" data-id="element-43">
        <div className="flex items-center gap-4" data-id="element-44">
          <span data-id="element-45">lease-agreement-2026.pdf</span>
          <span className="text-zinc-700" data-id="element-46">·</span>
          <span data-id="element-47">Page 1 of 1</span>
        </div>
        <div className="flex items-center gap-3" data-id="element-48">
          <span data-id="element-49">100%</span>
          <span className="text-zinc-700" data-id="element-50">·</span>
          <span className="text-violet-300/80" data-id="element-51">Smart Detection: ON</span>
        </div>
      </div>

      {/* PDF stage */}
      <div className="flex-1 overflow-auto thin-scroll p-8 flex justify-center" data-id="element-52">
        <div className="relative w-full max-w-[640px] aspect-[8.5/11] bg-[#f5f1ea] paper-grain rounded-sm shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] text-[#1a1a22]" data-id="element-53">
          {/* Document content */}
          <div className="absolute inset-0 p-10 text-[11px] leading-[1.7] font-serif" data-id="element-54">
            <div className="text-center mb-5" data-id="element-55">
              <div className="text-[18px] font-bold tracking-tight" data-id="element-56">
                RESIDENTIAL LEASE AGREEMENT
              </div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#1a1a22]/60 mt-1" data-id="element-57">
                Form RLA-204 · State of California
              </div>
            </div>

            <p className="mb-3" data-id="element-58">
              This Residential Lease Agreement ("Agreement") is made and entered
              into on this date by and between the Landlord and the Tenant
              identified below. Both parties agree to the terms set forth in
              this contract.
            </p>

            <div className="mt-5 mb-2 font-bold uppercase text-[10px] tracking-wider" data-id="element-59">
              1. Tenant Information
            </div>
            <div className="space-y-2.5" data-id="element-60">
              <div className="flex items-baseline gap-2" data-id="element-61">
                <span className="text-[#1a1a22]/70 shrink-0" data-id="element-62">Full Name:</span>
                <FieldBox id="name" fields={fields} activeId={activeId} values={values} onClick={onFieldClick} data-id="element-63" />
              </div>
              <div className="flex items-baseline gap-2" data-id="element-64">
                <span className="text-[#1a1a22]/70 shrink-0" data-id="element-65">
                  Date of Birth:
                </span>
                <FieldBox id="dob" fields={fields} activeId={activeId} values={values} onClick={onFieldClick} width="w-44" data-id="element-66" />
                <span className="text-[#1a1a22]/70 shrink-0 ml-3" data-id="element-67">Email:</span>
                <FieldBox id="email" fields={fields} activeId={activeId} values={values} onClick={onFieldClick} data-id="element-68" />
              </div>
              <div className="flex items-baseline gap-2" data-id="element-69">
                <span className="text-[#1a1a22]/70 shrink-0" data-id="element-70">Address:</span>
                <FieldBox id="address" fields={fields} activeId={activeId} values={values} onClick={onFieldClick} data-id="element-71" />
              </div>
            </div>

            <div className="mt-5 mb-2 font-bold uppercase text-[10px] tracking-wider" data-id="element-72">
              2. Lease Terms
            </div>
            <p className="mb-2" data-id="element-73">
              The lease shall commence on the start date specified below and
              continue for the duration indicated. Monthly rent shall be paid on
              the 1st day of each month.
            </p>
            <div className="space-y-2.5" data-id="element-74">
              <div className="flex items-baseline gap-2" data-id="element-75">
                <span className="text-[#1a1a22]/70 shrink-0" data-id="element-76">Start Date:</span>
                <FieldBox id="start" fields={fields} activeId={activeId} values={values} onClick={onFieldClick} width="w-44" data-id="element-77" />
                <span className="text-[#1a1a22]/70 shrink-0 ml-3" data-id="element-78">
                  Monthly Rent:
                </span>
                <FieldBox id="rent" fields={fields} activeId={activeId} values={values} onClick={onFieldClick} width="w-32" data-id="element-79" />
              </div>
            </div>

            <div className="mt-5 mb-2 font-bold uppercase text-[10px] tracking-wider" data-id="element-80">
              3. Acknowledgment & Signature
            </div>
            <p className="mb-3" data-id="element-81">
              By signing below, the Tenant acknowledges they have read,
              understood, and agree to all terms and conditions of this
              Agreement.
            </p>

            <div className="mt-4 flex items-end gap-3" data-id="element-82">
              <div className="flex-1" data-id="element-83">
                <div className="text-[#1a1a22]/70 mb-1" data-id="element-84">Signature:</div>
                <SignatureBox fields={fields} activeId={activeId} signatureDataUrl={signatureDataUrl} onClick={() => onFieldClick('signature')} data-id="element-85" />
              </div>
              <div className="text-[9px] text-[#1a1a22]/50 pb-1 font-sans" data-id="element-86">
                X ________________
              </div>
            </div>

            <div className="absolute bottom-4 left-10 right-10 flex justify-between text-[8px] text-[#1a1a22]/40 font-sans uppercase tracking-wider" data-id="element-87">
              <span data-id="element-88">RLA-204 · Rev 2026</span>
              <span data-id="element-89">Page 1 / 1</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function FieldBox({
  id,
  fields,
  activeId,
  values,
  onClick,
  width = 'flex-1'
}: {
  id: string;
  fields: FormField[];
  activeId: string | null;
  values: Record<string, string>;
  onClick: (id: string) => void;
  width?: string;
}) {
  const field = fields.find(f => f.id === id);
  if (!field) return null;
  const value = values[id];
  const filled = Boolean(value);
  const isActive = activeId === id;
  return <button onClick={() => onClick(id)} className={`${width} h-6 px-2 text-left text-[11px] font-sans transition-all ${filled && !isActive ? 'field-glow-filled text-[#1a1a22]' : isActive ? 'field-glow field-glow-active text-[#1a1a22]' : 'field-glow text-[#1a1a22]/40'}`} data-id="element-90">
      {filled ? value : <span className="italic" data-id="element-91">click to fill</span>}
    </button>;
}
function SignatureBox({
  fields,
  activeId,
  signatureDataUrl,
  onClick
}: {
  fields: FormField[];
  activeId: string | null;
  signatureDataUrl: string | null;
  onClick: () => void;
}) {
  const field = fields.find(f => f.id === 'signature');
  if (!field) return null;
  const filled = Boolean(signatureDataUrl);
  const isActive = activeId === 'signature';
  return <button onClick={onClick} className={`w-full h-14 transition-all flex items-center justify-center ${filled && !isActive ? 'field-glow-filled' : isActive ? 'field-glow field-glow-active' : 'field-glow'}`} data-id="element-92">
      {signatureDataUrl ? <img src={signatureDataUrl} alt="Signature" className="max-h-12 max-w-full object-contain" data-id="element-93" /> : <span className="text-[10px] italic text-[#1a1a22]/40 font-sans" data-id="element-94">
          click to sign
        </span>}
    </button>;
}